import { ChangeDetectionStrategy, Component, computed, input, type InputSignal, type Signal } from '@angular/core';
import { UiStateTone } from '../../types/ui-state-tone.type';

@Component({
  selector: 'ui-progress-bar',
  imports: [],
  templateUrl: './ui-progress-bar.component.html',
  styleUrl: './ui-progress-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiProgressBarComponent {
  public readonly value: InputSignal<number> = input<number>(0);
  public readonly max: InputSignal<number> = input<number>(100);
  public readonly ariaLabel: InputSignal<string> = input<string>('Postęp');
  public readonly tone: InputSignal<UiStateTone> = input<UiStateTone>('info');
  public readonly showValue: InputSignal<boolean> = input<boolean>(false);
  public readonly valueLabel: InputSignal<string | null> = input<string | null>(null);

  protected readonly normalizedMax: Signal<number> = computed((): number => Math.max(this.max(), 1));
  protected readonly normalizedValue: Signal<number> = computed((): number => {
    return Math.min(Math.max(this.value(), 0), this.normalizedMax());
  });
  protected readonly progressPercent: Signal<number> = computed((): number => {
    const rawPercent: number = (this.value() / this.normalizedMax()) * 100;

    return Math.min(Math.max(rawPercent, 0), 100);
  });
  protected readonly effectiveTone: Signal<UiStateTone> = computed((): UiStateTone => {
    if (this.tone() !== 'info') {
      return this.tone();
    }

    if (this.progressPercent() > 95) {
      return 'danger';
    }

    if (this.progressPercent() > 80) {
      return 'warning';
    }

    return 'info';
  });
  protected readonly progressText: Signal<string> = computed((): string => {
    return this.valueLabel() ?? `${Math.round(this.progressPercent())}%`;
  });
  protected readonly isNeutral: Signal<boolean> = computed((): boolean => this.effectiveTone() === 'neutral');
  protected readonly isInfo: Signal<boolean> = computed((): boolean => this.effectiveTone() === 'info');
  protected readonly isSuccess: Signal<boolean> = computed((): boolean => this.effectiveTone() === 'success');
  protected readonly isWarning: Signal<boolean> = computed((): boolean => this.effectiveTone() === 'warning');
  protected readonly isDanger: Signal<boolean> = computed((): boolean => this.effectiveTone() === 'danger');
}
